import type { VoidFunctionComponent } from 'react';

import { BannerAttention, BannerSize, BannerStyle } from '../../../../../typings/enums';
import utils from '../../../../utils';
import { Icon, icons, RouterLink } from '../../../generic';
import Banner from '../../../generic/Banner';
import Flex from '../../../generic/Flex';
import { useCanUserSetSBOMPolicy } from '../../settingsv2/permissions';
import SummaryActionLink from '../SummaryActionLink';
import type {
  SBOMAnalysisDependencyScan,
  SBOMAnalysisDetails,
  SBOMAnalysisFile,
  SBOMAnalysisRequiredFields,
  SBOMAnalysisStatus,
} from '../../../../../shared/sbomSummary/sbomAnalysis';

import { SBOMAnalysisCategory } from './SBOMAnalysisComponent';
import SBOMAnalysisItem from './SBOMAnalysisItem';

import styles from './SBOMAnalysis.module.less';

type SBOMAnalysisSectionProps = {
  category: SBOMAnalysisCategory;
  data: SBOMAnalysisFile | SBOMAnalysisRequiredFields | SBOMAnalysisDependencyScan;
  isSBOMAnalysisEnabled?: boolean;
  revisionBaseUrl?: string;
  projectLocator?: string;
  testId?: string;
};

const SBOMAnalysisSection: VoidFunctionComponent<SBOMAnalysisSectionProps> = ({
  category,
  data,
  isSBOMAnalysisEnabled,
  revisionBaseUrl,
  projectLocator = '/',
  testId,
}) => {
  if (!data) {
    return null;
  }

  const count = data?.analysis?.length;
  const grid = count === 1 ? 'col-lg-12' : 'col-lg-6';

  return (
    <div className="width-full" data-testid={testId}>
      <Flex justify="flex-start" gap={5} className={styles.sectionTitle}>
        <SectionIcon category={category} />
        <SectionTitle category={category} />
        <SectionLink category={category} revisionBaseUrl={revisionBaseUrl} />
      </Flex>
      <hr className="margin-top-sm margin-bottom-sm" />

      {category === SBOMAnalysisCategory.REQUIRED_FIELDS && (!isSBOMAnalysisEnabled || !data.analysis) &&
        <NoPolicyFallBack projectLocator={projectLocator} />}

      {data.analysis &&
        <>
          <SectionBanner category={category} status={data.status} />
          <div className="row">
            {data.analysis.map((analysisDetails: SBOMAnalysisDetails) => {
              return (
                <div className={grid} key={analysisDetails.description}>
                  <SBOMAnalysisItem item={analysisDetails} />
                  {count > 1 && <hr className="nomargin-v" />}
                </div>
              );
            })}
          </div>
        </> }
    </div>
  );
};

export default SBOMAnalysisSection;

/**
 * Analysis Section: Icon Component
 */
type SectionIconProps = { category: SBOMAnalysisCategory };
const SectionIcon: VoidFunctionComponent<SectionIconProps> = ({
  category,
}) => {
  const getIcon = () => {
    switch (category) {
      case SBOMAnalysisCategory.FILE:
        return icons.listTree;
      case SBOMAnalysisCategory.REQUIRED_FIELDS:
        return icons.tableTree;
      case SBOMAnalysisCategory.DEPENDENCY_SCAN:
      default:
        return icons.box;
    }
  };
  return (<Icon icon={getIcon()} />);
};

/**
 * Analysis Section: Title Component
 */
type SectionTitleProps = { category: SBOMAnalysisCategory, testId?: string };
const SectionTitle: VoidFunctionComponent<SectionTitleProps> = ({
  category,
  testId,
}) => {
  const getTitle = () => {
    if (category === SBOMAnalysisCategory.FILE) {
      return 'SBOM File';
    }

    if (category === SBOMAnalysisCategory.REQUIRED_FIELDS) {
      return 'Required Fields';
    }

    if (category === SBOMAnalysisCategory.DEPENDENCY_SCAN) {
      return 'Dependency Scan';
    }

    return '';
  };
  return (<span data-testid={testId}>{getTitle()}</span>);
};

/**
 * Analysis Section: Link Component
 */
type SectionLinkProps = { category: SBOMAnalysisCategory, revisionBaseUrl?: string };
const SectionLink: VoidFunctionComponent<SectionLinkProps> = ({
  category,
  revisionBaseUrl,
}) => {
  if (category !== SBOMAnalysisCategory.DEPENDENCY_SCAN) {
    return null;
  }
  if (!revisionBaseUrl) {
    return null;
  }
  return (
    <span className={styles.sectionLink}>
      <SummaryActionLink
        action="View dependencies"
        icon={icons.angleDoubleRight}
        to={`${revisionBaseUrl}/browse/dependencies`}
      />
    </span>);
};

/**
 * Analysis Section: Banner Component
 */
type SectionBannerProps = { category: SBOMAnalysisCategory, status: SBOMAnalysisStatus };
const SectionBanner: VoidFunctionComponent<SectionBannerProps> = ({
  category,
  status,
}) => {
  const getBannerStyle = () => {
    return status === 'success' ? BannerStyle.Success : BannerStyle.High;
  };

  const getBannerContent = () => {
    if (category === SBOMAnalysisCategory.FILE) {
      return status === 'success' ?
        'This SBOM meets all of FOSSA’s requirements for analysis.'
        :
        'FOSSA encountered problems reading the source SBOM file.';
    }

    if (category === SBOMAnalysisCategory.REQUIRED_FIELDS) {
      return status === 'success' ?
        'All required fields are included.'
        :
        'Required fields missing or encountered problems.';
    }

    return '';
  };

  if (category === SBOMAnalysisCategory.DEPENDENCY_SCAN) {
    return null;
  }

  return (<Banner
    bannerStyle={getBannerStyle()}
    bannerSize={BannerSize.Small}
    bannerAttention={BannerAttention.Low}
    title=""
    body={getBannerContent()}
  />);
};

const NoPolicyFallBack: VoidFunctionComponent<{ projectLocator: string }> = ({
  projectLocator,
}) => {
  const canSetSBOMPolicy = useCanUserSetSBOMPolicy(projectLocator);

  return (
    <Flex direction="column" align="center" gap={8} className={styles.noPolicyContainer}>
      <div className={styles.noPolicyTitle}>No policy is set</div>
      <div className={styles.noPolicyDescription}>Set a policy for SBOMs to require formats and fields.</div>
      {canSetSBOMPolicy &&
        <Flex direction="row" align="center" gap={10}>
          <RouterLink to={`/projects/${utils.nuc(projectLocator)}/settings/policies`}>
            <span>Set project policies</span>
          </RouterLink>
          <RouterLink to="/policies/sbom">
            <span>Create SBOM policies</span>
          </RouterLink>
        </Flex>}
    </Flex>
  );
};
